import React from "react";
import "./Strategies.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb,
  faLayerGroup,
  faRocket,
  faChartLine,
  faUsersGear,
  faArrowsSpin,
  faShieldHalved,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const strategyItems = [
  {
    icon: faLightbulb,
    pill: "Research",
    title: "Market Intelligence",
    description: "Deep audience and competitor research to find the opportunities that actually drive revenue.",
    bullets: ["Audience personas", "Competitor gaps", "Keyword research"],
  },
  {
    icon: faLayerGroup,
    pill: "Position",
    title: "Brand & Messaging",
    description: "Clear positioning and messaging that makes your offer stand out and resonates with your ideal customers.",
    bullets: ["Value proposition", "Brand voice", "Content pillars"],
  },
  {
    icon: faRocket,
    pill: "Launch",
    title: "Rapid Deployment",
    description: "Agile sprints that ship quality work on predictable timelines without cutting corners.",
    bullets: ["Sprint planning", "Quality assurance", "Go-live assistance"],
  },
  {
    icon: faChartLine,
    pill: "SEO",
    title: "Organic Growth",
    description: "Technical SEO and content strategies that compound over time and bring free, qualified traffic.",
    bullets: ["Technical SEO", "Content marketing", "Link building"],
  },
  {
    icon: faUsersGear,
    pill: "Target",
    title: "Paid Acquisition",
    description: "Google Ads and social campaigns tuned for ROI with proper tracking and conversion optimization.",
    bullets: ["Google & Meta ads", "Retargeting", "Landing page CRO"],
  },
  {
    icon: faArrowsSpin,
    pill: "Retain",
    title: "Customer Lifecycle",
    description: "Email sequences and retention strategies that turn one-time buyers into repeat customers.",
    bullets: ["Email automation", "Loyalty programs", "Upsell flows"],
  },
  {
    icon: faBolt,
    pill: "Automate",
    title: "Smart Automation",
    description: "CRM integrations and workflows that reduce manual work and speed up your sales process.",
    bullets: ["CRM setup", "Lead routing", "Auto-responders"],
  },
  {
    icon: faShieldHalved,
    pill: "Secure",
    title: "Trust & Compliance",
    description: "SSL, privacy policies, and secure forms that protect your customers and build credibility.",
    bullets: ["SSL certificates", "GDPR compliance", "Secure hosting"],
  },
];

const Strategies = () => {
  return (
    <section className="strat" aria-label="Growth strategies">
      <div className="strat__wrap">
        <motion.div
          className="strat__head"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
        >
          <div>
            <div className="strat__badge">
              <span className="dot" aria-hidden="true" />
              Growth Playbook
            </div>
            <h2 className="strat__title">
              Strategies that <span>grow</span> your business online.
            </h2>
            <p className="strat__sub">
              Research, positioning, execution, and measurement — every tactic is
              chosen to reduce waste and maximize your return on investment.
            </p>
          </div>
          <div className="strat__note">
            <b>Our philosophy</b>
            <p>We build sustainable growth engines, not one-off campaigns that fizzle out.</p>
          </div>
        </motion.div>

        <div className="strat__grid">
          {strategyItems.map((item, index) => (
            <motion.div
              className="strat__card"
              key={index}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp}
              transition={{ delay: index * 0.05 }}
            >
              <div className="strat__top">
                <div className="strat__icon">
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="strat__pill">{item.pill}</div>
              </div>
              <h3 className="strat__h3">{item.title}</h3>
              <p className="strat__p">{item.description}</p>
              <ul className="strat__bullets">
                {item.bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Strategies;
